import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useForm, useWatch } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Check } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { CloseButton } from "./ModalHeader";
import {
  checkPasswordRules,
  hasSequentialChars,
  isPasswordFormatValid,
  isPasswordSimilarToLoginId,
  type PasswordRuleState,
} from "@/common/utils/password";

export type PasswordChangeVariant = "first-login" | "change";

interface PasswordChangeForm {
  password: string;
  passwordConfirm: string;
}

const DEFAULT_VALUES: PasswordChangeForm = {
  password: "",
  passwordConfirm: "",
};

// 제출 검증 에러 메세지 (디자인 스펙)
const ERROR_FORMAT = "비밀번호 형식에 맞게 입력해 주세요.";
const ERROR_SIMILAR_ID = "아이디와 유사한 비밀번호는 사용할 수 없어요.";
const ERROR_SEQUENTIAL = "연속된 문자를 포함할 수 없어요.";
const ERROR_MISMATCH = "입력한 비밀번호와 일치하지 않아요.";
const ERROR_SUBMIT_FAILED = "비밀번호 변경에 실패했어요. 잠시 후 다시 시도해 주세요.";

const RULE_LABELS: { key: keyof PasswordRuleState; label: string }[] = [
  { key: "length", label: "8자 ~ 64자" },
  { key: "letter", label: "영문 포함" },
  { key: "number", label: "숫자 포함" },
  { key: "special", label: "특수문자 포함" },
];

const VARIANT_TEXT: Record<
  PasswordChangeVariant,
  { title: string; description?: string }
> = {
  "first-login": {
    title: "임시 비밀번호를 변경해 주세요",
    description: "보안을 위해 새 비밀번호로 변경해 주세요",
  },
  change: {
    title: "비밀번호 변경",
  },
};

export interface PasswordChangeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** first-login: 닫기 불가(임시 비밀번호 강제 변경), change: 닫기 가능 */
  variant?: PasswordChangeVariant;
  /** 로그인 아이디(이메일) — 아이디 포함 비밀번호 차단용 */
  loginId?: string | null;
  /** 저장 처리. 성공 시 resolve, 실패 시 throw. BE 연동은 이후 주입 */
  onSubmit?: (newPassword: string) => Promise<void> | void;
}

/**
 * 비밀번호 변경 모달
 * - variant="first-login": 최초 로그인/재설정 후 강제 변경 (닫기 버튼·외부 클릭 닫기 없음)
 * - variant="change": 프로필 메뉴에서 진입하는 단순 변경 (닫기 가능)
 */
export function PasswordChangeModal({
  open,
  onOpenChange,
  variant = "change",
  loginId,
  onSubmit,
}: PasswordChangeModalProps) {
  const dismissible = variant === "change";
  const { title, description } = VARIANT_TEXT[variant];

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PasswordChangeForm>({ defaultValues: DEFAULT_VALUES });

  // watch() 대신 useWatch — React Compiler 가 컴포넌트를 스킵하지 않도록
  const password = useWatch({ control, name: "password" });
  const passwordConfirm = useWatch({ control, name: "passwordConfirm" });
  const rules = checkPasswordRules(password ?? "");

  // 열고 닫을 때마다 폼 초기화
  React.useEffect(() => {
    if (!open) reset(DEFAULT_VALUES);
  }, [open, reset]);

  const canSubmit = !!password && !!passwordConfirm && !isSubmitting;

  const handleClose = () => onOpenChange(false);

  const onValid = async (data: PasswordChangeForm) => {
    // 형식 오류와 확인 불일치가 동시에 나면 두 필드 모두 표시해야 하므로 에러를 누적한다
    const passwordError = !isPasswordFormatValid(data.password)
      ? ERROR_FORMAT
      : isPasswordSimilarToLoginId(data.password, loginId)
        ? ERROR_SIMILAR_ID
        : hasSequentialChars(data.password)
          ? ERROR_SEQUENTIAL
          : null;
    const confirmError =
      data.password !== data.passwordConfirm ? ERROR_MISMATCH : null;

    if (passwordError) {
      setError("password", { message: passwordError }, { shouldFocus: true });
    }
    if (confirmError) {
      // 비밀번호 필드 에러가 없을 때만 확인 필드로 포커스
      setError(
        "passwordConfirm",
        { message: confirmError },
        { shouldFocus: !passwordError },
      );
    }
    if (passwordError || confirmError) return;

    try {
      await onSubmit?.(data.password);
      handleClose();
    } catch (error) {
      // 서버 재검증(400) 문구는 형식·아이디·연속 위반이므로 비밀번호 필드에 표시
      setError(
        "password",
        {
          message: error instanceof Error ? error.message : ERROR_SUBMIT_FAILED,
        },
        { shouldFocus: true },
      );
    }
  };

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        // first-login 은 사용자가 임의로 닫을 수 없다
        if (!next && !dismissible) return;
        onOpenChange(next);
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-bg-dim",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          onEscapeKeyDown={(e) => {
            if (!dismissible) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (!dismissible) e.preventDefault();
          }}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-120",
            "-translate-x-1/2 -translate-y-1/2",
            "bg-bg-primary rounded-2xl overflow-hidden",
            "inset-border-normal",
            "shadow-[0px_0px_80px_0px_rgba(0,0,0,0.25)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "outline-none",
          )}
        >
          <form
            onSubmit={handleSubmit(onValid)}
            className="flex flex-col gap-4"
          >
            {/* Header — 닫기 버튼 (change 변형에서만) */}
            <div className="flex justify-end px-6 pt-5">
              {dismissible && <CloseButton onClose={handleClose} />}
            </div>

            {/* 제목 + (최초 로그인) 부제 */}
            <div className="flex flex-col gap-3 px-6">
              <DialogPrimitive.Title className="typo-title-3-bold text-labels-primary">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="typo-body-2-medium text-labels-alternative">
                  {description}
                </DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">
                  {title}
                </DialogPrimitive.Description>
              )}
            </div>

            {/* 입력 필드 — 기본 높이 224px, 에러 메시지가 뜨면 늘어난다 */}
            <div className="flex min-h-56 flex-col gap-5 px-6">
              {/* 비밀번호 */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="password-change-new"
                  className="typo-label-1-medium text-labels-primary"
                >
                  비밀번호
                </label>
                <Input
                  id="password-change-new"
                  type="password"
                  autoComplete="new-password"
                  maxLength={64}
                  placeholder="비밀번호를 입력해 주세요."
                  aria-invalid={!!errors.password}
                  {...register("password")}
                />
                {/* 에러 메시지와 조건 칩은 동시에 표시한다 (디자인 스펙) */}
                {errors.password && (
                  <span className="typo-caption-1-medium text-accent-mute-red">
                    {errors.password.message}
                  </span>
                )}
                <div className="flex flex-wrap gap-2">
                  {RULE_LABELS.map(({ key, label }) => {
                    const met = rules[key];
                    return (
                      <div key={key} className="flex items-center gap-1">
                        <Check
                          size={16}
                          color={met ? "primary-normal" : "labels-assistive"}
                        />
                        <span
                          className={cn(
                            "typo-label-1-medium",
                            met
                              ? "text-primary-normal"
                              : "text-labels-assistive",
                          )}
                        >
                          {label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 비밀번호 확인 */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="password-change-confirm"
                  className="typo-label-1-medium text-labels-primary"
                >
                  비밀번호 확인
                </label>
                <Input
                  id="password-change-confirm"
                  type="password"
                  autoComplete="new-password"
                  maxLength={64}
                  placeholder="비밀번호를 한 번 더 입력해 주세요."
                  aria-invalid={!!errors.passwordConfirm}
                  {...register("passwordConfirm")}
                />
                {errors.passwordConfirm && (
                  <span className="typo-caption-1-medium text-accent-mute-red">
                    {errors.passwordConfirm.message}
                  </span>
                )}
              </div>
            </div>

            {/* 제출 버튼 */}
            <div className="px-6 pt-2 pb-6">
              <button
                type="submit"
                disabled={!canSubmit}
                className={cn(
                  "w-full flex items-center justify-center",
                  "px-4.5 py-3 rounded-xl",
                  "typo-body-1-semibold",
                  "inset-border-double",
                  "relative overflow-hidden",
                  canSubmit
                    ? [
                        "bg-primary-gradient text-static-white",
                        "after:absolute after:inset-0 after:bg-static-black",
                        "after:opacity-0 hover:after:opacity-[0.06]",
                        "after:transition-opacity",
                      ]
                    : "bg-fills-neutral text-labels-assistive cursor-not-allowed",
                )}
              >
                <span className="relative z-10">
                  {isSubmitting ? "변경 중..." : "비밀번호 변경"}
                </span>
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

PasswordChangeModal.displayName = "PasswordChangeModal";
